import { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  AdReview,
  ReviewCapability,
  ReviewProposedFix,
  ReviewIssue,
} from '@agency-smm/shared';
import { Button } from './Button';

// ─── Animations ─────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const dotBounce = keyframes`
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
`;

// ─── Styled Components ──────────────────────────────────────────────────────

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  padding: 4px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 2px; }
`;

const MsgRow = styled.div<{ $from: 'novah' | 'user' }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  animation: ${fadeIn} 0.3s ease;
  justify-content: ${({ $from }) => $from === 'user' ? 'flex-end' : 'flex-start'};
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(59, 130, 246, 0.25);
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const Bubble = styled.div<{ $from: 'novah' | 'user' }>`
  max-width: 85%;
  padding: 8px 12px;
  border-radius: ${({ $from }) => ($from === 'novah' ? '4px 12px 12px 12px' : '12px 4px 12px 12px')};
  font-size: 13px;
  line-height: 1.5;
  background: ${({ $from, theme }) => $from === 'novah' ? theme.colors.background : theme.colors.primary};
  color: ${({ $from }) => $from === 'user' ? 'white' : 'inherit'};
  border: ${({ $from, theme }) => $from === 'novah' ? theme.borders.thin : 'none'};
`;

const TypingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px 0;
  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #94a3b8;
    animation: ${dotBounce} 1.2s infinite ease-in-out;
    &:nth-child(2) { animation-delay: 0.16s; }
    &:nth-child(3) { animation-delay: 0.32s; }
  }
`;

const ScoreDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px 0;
`;

const ScoreBig = styled.span<{ $color: string }>`
  font-size: 36px;
  font-weight: 800;
  color: ${({ $color }) => $color};
`;

const ScoreLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const IssueCard = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 10px;
  margin: 6px 0;
`;

const SeverityDot = styled.span<{ $severity: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  background: ${({ $severity }) =>
    $severity === 'critical' ? '#ef4444' :
    $severity === 'warning' ? '#f59e0b' : '#3b82f6'};
`;

const IssueActions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 8px;
`;

const StatusMsg = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-style: italic;
  margin: 4px 0;
`;

const WidgetWrap = styled.div`
  animation: ${fadeIn} 0.3s ease;
  margin-bottom: 8px;
`;

// ─── Props ──────────────────────────────────────────────────────────────────

interface NovahReviewChatProps {
  review: AdReview | null;
  isLoading: boolean;
  error: string | null;
  reviewCapability: ReviewCapability;
  onRequestReview: () => void;
  onApplyFix: (issueIndex: number, fix: ReviewProposedFix) => void;
  onApplyAllFixes: () => void;
  onRetry: () => void;
  onNavigateBilling?: () => void;
  previousScore?: number;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

type Step = 'idle' | 'analyzing' | 'results' | 'done';

const ANALYZING_MESSAGES = [
  'Checking text readability...',
  'Analyzing composition...',
  'Evaluating color harmony...',
  'Reviewing CTA visibility...',
  'Checking logo placement...',
  'Assessing overall quality...',
];

function getScoreColor(score: number): string {
  if (score >= 90) return '#10b981';
  if (score >= 70) return '#f59e0b';
  if (score >= 50) return '#f97316';
  return '#ef4444';
}

function getScoreMessage(score: number, issueCount: number): string {
  if (score >= 90) {
    return issueCount === 0
      ? 'Your ad looks great! Ready to publish.'
      : `Looking good! Just ${issueCount} minor thing${issueCount > 1 ? 's' : ''} to polish.`;
  }
  if (score >= 70) {
    return `Not bad! I found ${issueCount} issue${issueCount > 1 ? 's' : ''} you might want to fix.`;
  }
  if (score >= 50) {
    return `There are ${issueCount} issue${issueCount > 1 ? 's' : ''} that should be addressed before publishing.`;
  }
  return `I found ${issueCount} significant issue${issueCount > 1 ? 's' : ''} that need attention.`;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function NovahReviewChat({
  review,
  isLoading,
  error,
  reviewCapability,
  onRequestReview,
  onApplyFix,
  onApplyAllFixes,
  onRetry,
  onNavigateBilling,
  previousScore,
}: NovahReviewChatProps) {
  const [step, setStep] = useState<Step>('idle');
  const [analyzingIdx, setAnalyzingIdx] = useState(0);
  const [appliedFixes, setAppliedFixes] = useState<Set<number>>(new Set());
  const [animatedScore, setAnimatedScore] = useState(0);
  const [expandedIssues, setExpandedIssues] = useState<Set<number>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  const canFix = reviewCapability.tier === 'review_and_fix';

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [step, review, error, appliedFixes.size]);

  // Transition to analyzing when loading starts
  useEffect(() => {
    if (isLoading) setStep('analyzing');
  }, [isLoading]);

  // Transition to results when review arrives
  useEffect(() => {
    if (review && !isLoading) {
      setStep('results');
      setAppliedFixes(new Set());
    }
  }, [review, isLoading]);

  // Auto-expand first issue when review arrives
  useEffect(() => {
    if (review && review.issues.length > 0) {
      setExpandedIssues(new Set([0]));
    }
  }, [review]);

  // Rotating analyzing messages
  useEffect(() => {
    if (step !== 'analyzing') {
      setAnalyzingIdx(0);
      return;
    }
    const interval = setInterval(() => {
      setAnalyzingIdx(prev => (prev + 1) % ANALYZING_MESSAGES.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [step]);

  // Score counter animation
  useEffect(() => {
    if (!review || step !== 'results') return;
    const target = review.score;
    const duration = 1000;
    const start = Date.now();
    const frame = () => {
      const progress = Math.min((Date.now() - start) / duration, 1);
      setAnimatedScore(Math.round(target * progress));
      if (progress < 1) requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }, [review, step]);

  const handleApplyFix = useCallback((index: number, fix: ReviewProposedFix) => {
    onApplyFix(index, fix);
    setAppliedFixes(prev => new Set([...prev, index]));
  }, [onApplyFix]);

  const handleApplyAll = useCallback(() => {
    onApplyAllFixes();
    if (review) {
      const allFixable = new Set(
        review.issues
          .map((issue, i) => (issue.proposedFix ? i : -1))
          .filter(i => i >= 0)
      );
      setAppliedFixes(allFixable);
    }
  }, [onApplyAllFixes, review]);

  const fixableCount = review?.issues.filter(
    (issue, i) => issue.proposedFix && !appliedFixes.has(i)
  ).length ?? 0;

  return (
    <ChatContainer>
      {/* Novah greeting */}
      <MsgRow $from="novah">
        <Avatar><img src="/novah_laugh.png" alt="Novah" /></Avatar>
        <Bubble $from="novah">
          {step === 'idle' && !error
            ? "Want me to review your ad design? I'll check readability, composition, and visual quality."
            : step === 'idle' && error
            ? error
            : step === 'analyzing'
            ? "Let me take a closer look..."
            : review
            ? getScoreMessage(review.score, review.issues.length)
            : "Ready when you are!"}
        </Bubble>
      </MsgRow>

      {/* Idle state: Analyze button */}
      {step === 'idle' && !error && (
        <WidgetWrap>
          {reviewCapability.tier === 'none' ? (
            <div style={{ textAlign: 'center', padding: '8px' }}>
              <p style={{ fontSize: 12, color: '#94a3b8', margin: '0 0 8px 0' }}>AI Review requires a Pro plan</p>
              {onNavigateBilling && <Button $size="sm" onClick={onNavigateBilling}>Upgrade to Pro</Button>}
            </div>
          ) : reviewCapability.reviewsRemaining <= 0 ? (
            <div style={{ textAlign: 'center', padding: '8px' }}>
              <p style={{ fontSize: 12, color: '#f59e0b', margin: '0 0 8px 0' }}>Monthly review limit reached</p>
              {onNavigateBilling && <Button $size="sm" $variant="outline" onClick={onNavigateBilling}>Upgrade Plan</Button>}
            </div>
          ) : (
            <Button $size="sm" $fullWidth onClick={onRequestReview}>
              Analyze My Ad
            </Button>
          )}
          <StatusMsg>
            Reviews: {reviewCapability.reviewsUsed} / {reviewCapability.reviewLimit === 9999 ? '\u221E' : reviewCapability.reviewLimit}
          </StatusMsg>
        </WidgetWrap>
      )}

      {/* Error state */}
      {step === 'idle' && error && (
        <WidgetWrap>
          <Button $size="sm" $fullWidth onClick={onRetry}>Try Again</Button>
        </WidgetWrap>
      )}

      {/* Analyzing state */}
      {step === 'analyzing' && (
        <WidgetWrap>
          <MsgRow $from="novah">
            <Avatar><img src="/novah_laugh.png" alt="Novah" /></Avatar>
            <Bubble $from="novah">
              <TypingDots><span /><span /><span /></TypingDots>
            </Bubble>
          </MsgRow>
          <StatusMsg>{ANALYZING_MESSAGES[analyzingIdx]}</StatusMsg>
        </WidgetWrap>
      )}

      {/* Results state */}
      {step === 'results' && review && (
        <>
          {/* Score display */}
          <WidgetWrap>
            <ScoreDisplay>
              <ScoreBig $color={getScoreColor(review.score)}>{animatedScore}</ScoreBig>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>/ 100</div>
                <ScoreLabel>Quality Score</ScoreLabel>
                {previousScore !== undefined && previousScore !== review.score && (
                  <div style={{
                    fontSize: 11,
                    color: review.score > previousScore ? '#10b981' : '#ef4444',
                    fontWeight: 600,
                  }}>
                    {review.score > previousScore ? '+' : ''}{review.score - previousScore} pts
                  </div>
                )}
              </div>
            </ScoreDisplay>
          </WidgetWrap>

          {/* No issues */}
          {review.issues.length === 0 && (
            <MsgRow $from="novah">
              <Avatar><img src="/novah_laugh.png" alt="Novah" /></Avatar>
              <Bubble $from="novah">
                No issues found! Your ad looks publish-ready.
              </Bubble>
            </MsgRow>
          )}

          {/* Issue cards (expand/collapse) */}
          {review.issues.map((issue: ReviewIssue, index: number) => (
            <WidgetWrap key={index}>
              <IssueCard onClick={() => setExpandedIssues(prev => {
                const next = new Set(prev);
                if (next.has(index)) next.delete(index); else next.add(index);
                return next;
              })} style={{ cursor: 'pointer' }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>
                  <SeverityDot $severity={issue.severity} />
                  {issue.severity.toUpperCase()} &mdash; {issue.type.replace(/_/g, ' ')}
                  <span style={{ float: 'right', fontSize: 10, color: '#9ca3af' }}>
                    {expandedIssues.has(index) ? '\u25B2' : '\u25BC'}
                  </span>
                </div>
                {expandedIssues.has(index) && (
                  <>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6, marginBottom: 4 }}>{issue.description}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>{issue.suggestion}</div>
                    {canFix && issue.proposedFix && !appliedFixes.has(index) && (
                      <IssueActions>
                        <Button $size="sm" $variant="outline" onClick={(e) => { e.stopPropagation(); handleApplyFix(index, issue.proposedFix!); }}>
                          {(issue.proposedFix.confidence ?? 1) >= 0.7 ? 'Apply Fix' : 'Try Fix'}
                        </Button>
                      </IssueActions>
                    )}
                    {appliedFixes.has(index) && (
                      <div style={{ fontSize: 11, color: '#10b981', marginTop: 6, fontWeight: 600 }}>Fixed</div>
                    )}
                  </>
                )}
              </IssueCard>
            </WidgetWrap>
          ))}

          {/* Apply all + Re-analyze */}
          {review.issues.length > 0 && (
            <WidgetWrap>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {canFix && fixableCount > 1 && (
                  <Button $size="sm" onClick={handleApplyAll}>
                    Apply All ({fixableCount})
                  </Button>
                )}
                <Button $size="sm" $variant="outline" onClick={() => { setStep('idle'); onRequestReview(); }}>
                  Re-analyze
                </Button>
              </div>
            </WidgetWrap>
          )}
        </>
      )}

      <div ref={scrollRef} />
    </ChatContainer>
  );
}
